// Open Haunt (wave 11): "Visit three haunts, light up your pumpkin" progress from accounts' `GET /v1/me/unlocks`, and
// Halloween night from the rooms calendar. Pure helpers; pages/haunt.ts draws them.

import type { Calendar, PlazaEvent } from "./events.ts";
import { HALLOWEEN_ROW } from "./rows.ts";

export const HAUNT_QUEST = "halloween-2026-visit-three-haunts";
export const LIT_UNLOCK = "halloween-2026-pumpkin-head-lit";

export interface QuestProgress {
  needed: number;
  /** game ids that count (≥ minSeconds of active play each, in the Halloween row) */
  counted: string[];
  done: boolean;
  minSeconds: number;
  /** unix seconds */
  from: number;
  to: number;
}

/** The pumpkin quest from `/me/unlocks`, or null when accounts doesn't have it. Having the unlock counts as done. */
export function questFrom(raw: unknown): QuestProgress | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as { quests?: unknown; unlocks?: unknown };
  const q = Array.isArray(r.quests) ? (r.quests.find((x) => x && typeof x === "object" && (x as { id?: unknown }).id === HAUNT_QUEST) as Record<string, unknown> | undefined) : undefined;
  if (!q) return null;
  const has = Array.isArray(r.unlocks) && r.unlocks.some((u) => (typeof u === "string" ? u : (u as { id?: unknown })?.id) === LIT_UNLOCK);
  const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);
  const needed = Math.max(1, num(q.needed, 3));
  const counted = Array.isArray(q.counted) ? q.counted.filter((x): x is string => typeof x === "string") : [];
  return { needed, counted, done: q.done === true || has || counted.length >= needed, minSeconds: num(q.minSeconds, 180), from: num(q.from, 0), to: num(q.to, 0) };
}

/** The line under the pumpkin: where the player is. */
export function progressText(p: QuestProgress, nowSec = Date.now() / 1000): string {
  if (p.done) return "Your pumpkin is lit. Wear the candlelit pumpkin head in Banger.";
  // accounts' clock decides what counts; this browser's clock only words the empty state
  if (p.from && nowSec < p.from && p.counted.length === 0) return "Haunts start counting on 23 October.";
  if (p.to && nowSec >= p.to) return `You visited ${Math.min(p.counted.length, p.needed)} of ${p.needed} haunts.`;
  const left = p.needed - p.counted.length;
  return `${p.counted.length} of ${p.needed} haunts visited. ${left} to go.`;
}

/** Minutes per haunt, for the copy's `{{rows: minutes}}` (whole minutes, as accounts counts seconds). */
export function minutesOf(p: QuestProgress | null): string | undefined {
  if (!p || !(p.minSeconds > 0)) return undefined;
  const m = p.minSeconds / 60;
  return Number.isInteger(m) ? String(m) : undefined;
}

/** Halloween night: the calendar's event that loops the Halloween row (or hands out the lit pumpkin). */
export function halloweenNight(cal: Calendar): PlazaEvent | null {
  return cal.events.find((e) => e.showcase === HALLOWEEN_ROW) ?? cal.events.find((e) => e.reward === LIT_UNLOCK) ?? null;
}

/** "Sat 31 Oct at 20:00 GMT" in the viewer's zone, for `{{world: plaza night time}}`. */
export function nightTime(e: Pick<PlazaEvent, "at">, timeZone?: string, locale = "en-GB"): string {
  return new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone, timeZoneName: "short" }).format(e.at);
}
